import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { DEFAULT_LOCALE, translate } from '@formosa/shared/i18n/translate';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { journeyApi } from '../../api/journeyApi';
import { journeyAccommodationApi } from '../../api/journeyAccommodationApi';
import { journeyChecklistApi } from '../../api/journeyChecklistApi';
import { journeyDecisionApi } from '../../api/journeyDecisionApi';
import { journeyPlanningApi } from '../../api/journeyPlanningApi';
import { journeySituationApi } from '../../api/journeySituationApi';
import { journeyTransportApi } from '../../api/journeyTransportApi';
import { useAuth } from '../../auth/AuthContext';
import { ErrorState, LoadingState } from '../../components/ui';
import {
  DECISIONS_PRODUCT_CHROME,
  decisionStatusProductLabel,
  WORKSPACE_PRODUCT_CHROME,
} from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import { useJourneyNoticeUnreadCount } from '../journey-notice/useJourneyNoticeUnreadCount';
import { ACTIVITY_TYPES, normalizePlanningSchedule, planningConflictMessage } from '../journey-planning/planningModel';
import {
  ACTIVITY_EXECUTION_ACTIONS,
  classifyActivityExecutionError,
  resolveActivityExecutionCapabilities,
} from '../journey-planning/activityExecutionModel';
import {
  EXTERNAL_MAP_NAVIGATION_LINK,
  isActivityExternalMapNavigationEligible,
  resolveExternalMapNavigation,
} from '../journey-planning/externalMapNavigation';
import { normalizeAccommodationSummary } from '../journey-accommodation/accommodationModel';
import { normalizeTransportSummary } from '../journey-transport/transportModel';
import { normalizeChecklistBoard } from '../journey-checklist/checklistModel';
import { normalizeDecisionList } from '../journey-decision/journeyDecisionModel';
import { unwrapSituationList, situationsNeedingAttention } from '../journey-situation/journeySituationModel';
import { WRITABLE_MEMORY_LIFECYCLES } from '../journey-journal/journeyMemoryModel';
import {
  buildLifecycleMutationBody,
  classifyLifecycleMutationError,
  lifecycleActionLabel,
  lifecycleConfirmCopy,
  lifecycleMutationErrorMessage,
} from '../journey-workspace/journeyLifecycleModel';
import OfflineReadBanner from '../offline/OfflineReadBanner';
import {
  OFFLINE_FRESHNESS,
  OFFLINE_READ_COPY,
} from '../offline/offlineFreshness';
import {
  clearJourneyOfflineSnapshotsAfterAuthoritativeDenial,
  mayUseJourneyOfflineFallback,
  readTodayOfflineSnapshot,
  writeTodayOfflineSnapshot,
} from '../offline/offlineJourneyRead';
import { resolveJourneyClock } from './todayClock';
import {
  activitiesForSelectedDay,
  activityExecutionActionLabel,
  activityStatusLabel,
  checklistItemsForToday,
  decisionsNeedingAttention,
  journalComposeHref,
  selectJourneyDay,
  selectNextActivity,
  staysForCalendarDate,
  TODAY_ACTIVITY_EXECUTION_COPY,
  todayJourneyLifecycleAction,
  todayModeCopy,
  transportsForCalendarDate,
} from './journeyTodayModel';
import '../journey-workspace/journey-workspace.css';
import '../journey-notice/journey-notice.css';
import './journey-today.css';

const ACTIVITY_TYPE_LABELS = Object.fromEntries(
  ACTIVITY_TYPES.map((item) => [item.value, item.label]),
);

function activityTypeLabel(value) {
  const key = String(value || '');
  return ACTIVITY_TYPE_LABELS[key] || key || 'Activity';
}

function unwrapList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

function sectionState(result, fallbackMessage) {
  if (result.status === 'fulfilled') {
    return { status: 'ready', data: result.value, error: '' };
  }
  // Never surface untrusted backend prose in section error slots.
  return {
    status: 'error',
    data: null,
    error: fallbackMessage,
  };
}

function executionFailureMessage(kind, cause) {
  if (kind === 'conflict') {
    return planningConflictMessage(cause) || TODAY_ACTIVITY_EXECUTION_COPY.conflict;
  }
  if (kind === 'validation') return TODAY_ACTIVITY_EXECUTION_COPY.validation;
  if (kind === 'forbidden' || kind === 'unauthorized') return TODAY_ACTIVITY_EXECUTION_COPY.forbidden;
  if (kind === 'not-found') return TODAY_ACTIVITY_EXECUTION_COPY.notFound;
  const raw = cause?.raw?.message || cause?.message || '';
  if (!raw || /failed to fetch|networkerror|network/i.test(raw)) {
    return TODAY_ACTIVITY_EXECUTION_COPY.network;
  }
  return TODAY_ACTIVITY_EXECUTION_COPY.generic;
}

function successMessageForAction(action) {
  if (action === ACTIVITY_EXECUTION_ACTIONS.CONFIRM) return TODAY_ACTIVITY_EXECUTION_COPY.successConfirm;
  if (action === ACTIVITY_EXECUTION_ACTIONS.START) return TODAY_ACTIVITY_EXECUTION_COPY.successStart;
  if (action === ACTIVITY_EXECUTION_ACTIONS.COMPLETE) return TODAY_ACTIVITY_EXECUTION_COPY.successComplete;
  if (action === ACTIVITY_EXECUTION_ACTIONS.CANCEL) return TODAY_ACTIVITY_EXECUTION_COPY.successCancel;
  return TODAY_ACTIVITY_EXECUTION_COPY.working;
}

export default function JourneyTodayPage({ now, deviceTimeZone } = {}) {
  const { journeyId } = useParams();
  const { user } = useAuth();
  const t = useCallback((key) => translate(DEFAULT_LOCALE, key), []);
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const executionLock = useRef(false);
  const lifecycleLock = useRef(false);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [errorModel, setErrorModel] = useState(null);
  const [journey, setJourney] = useState(null);
  const [scheduleState, setScheduleState] = useState({ status: 'loading', data: null, error: '' });
  const [staysState, setStaysState] = useState({ status: 'loading', data: [], error: '' });
  const [transportsState, setTransportsState] = useState({ status: 'loading', data: [], error: '' });
  const [checklistState, setChecklistState] = useState({ status: 'loading', data: [], error: '' });
  const [decisionsState, setDecisionsState] = useState({ status: 'loading', data: [], error: '' });
  const [situationsState, setSituationsState] = useState({ status: 'loading', data: [], error: '' });
  const [historicalDayId, setHistoricalDayId] = useState('');
  const [executionBusyActivityId, setExecutionBusyActivityId] = useState('');
  const [executionFeedback, setExecutionFeedback] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [lifecyclePending, setLifecyclePending] = useState(false);
  const [lifecycleFeedback, setLifecycleFeedback] = useState(null);
  const [lifecycleConfirmAction, setLifecycleConfirmAction] = useState(null);
  const [readSource, setReadSource] = useState('live');
  const [offlineFreshness, setOfflineFreshness] = useState(null);
  const [offlineFetchedAt, setOfflineFetchedAt] = useState('');
  const { unreadCount } = useJourneyNoticeUnreadCount(journeyId);
  const daySelectId = useId();
  const cancelReasonId = useId();
  const cancelTitleId = useId();
  const lifecycleConfirmTitleId = useId();
  const offlineCached = readSource === 'cached';

  const applyCachedToday = useCallback((record, freshness) => {
    const payload = record.payload || {};
    const normalized = normalizeJourneyWorkspace(payload.journey, viewerMemberId);
    setJourney(normalized);
    setScheduleState({
      status: 'ready',
      data: normalizePlanningSchedule(payload.schedule),
      error: '',
    });
    if (payload.stays != null) {
      setStaysState({
        status: 'ready',
        data: unwrapList(payload.stays).map(normalizeAccommodationSummary),
        error: '',
      });
    } else {
      setStaysState({
        status: 'error',
        data: [],
        error: OFFLINE_READ_COPY.networkRequired,
      });
    }
    if (payload.transports != null) {
      setTransportsState({
        status: 'ready',
        data: unwrapList(payload.transports).map(normalizeTransportSummary),
        error: '',
      });
    } else {
      setTransportsState({
        status: 'error',
        data: [],
        error: OFFLINE_READ_COPY.networkRequired,
      });
    }
    if (payload.checklist != null) {
      setChecklistState({
        status: 'ready',
        data: normalizeChecklistBoard(payload.checklist).items,
        error: '',
      });
    } else {
      setChecklistState({ status: 'ready', data: [], error: '' });
    }
    if (payload.decisions != null) {
      setDecisionsState({
        status: 'ready',
        data: normalizeDecisionList(payload.decisions),
        error: '',
      });
    } else {
      setDecisionsState({ status: 'ready', data: [], error: '' });
    }
    if (payload.situations != null) {
      setSituationsState({
        status: 'ready',
        data: unwrapSituationList(payload.situations),
        error: '',
      });
    } else {
      setSituationsState({ status: 'ready', data: [], error: '' });
    }
    setReadSource('cached');
    setOfflineFreshness(freshness);
    setOfflineFetchedAt(record.fetchedAt || '');
    setPhase('ready');
  }, [viewerMemberId]);

  const load = useCallback(async () => {
    const requestId = ++generation.current;
    const scopedId = String(journeyId || '');
    setPhase('loading');
    setError('');
    setErrorModel(null);
    try {
      const [
        journeyResult,
        scheduleResult,
        staysResult,
        transportsResult,
        checklistResult,
        decisionsResult,
        situationsResult,
      ] = await Promise.allSettled([
        journeyApi.getDetail(scopedId),
        journeyPlanningApi.getSchedule(scopedId),
        journeyAccommodationApi.list(scopedId),
        journeyTransportApi.list(scopedId),
        journeyChecklistApi.getBoard(scopedId),
        journeyDecisionApi.list(scopedId),
        journeySituationApi.list(scopedId),
      ]);
      if (requestId !== generation.current) return;
      if (String(journeyId) !== scopedId) return;

      if (journeyResult.status === 'rejected') {
        const cause = journeyResult.reason;
        const kind = classifyJourneyAccessError(cause);
        if (kind === 'not-found' || kind === 'forbidden') {
          setReadSource('live');
          if (viewerMemberId) {
            await clearJourneyOfflineSnapshotsAfterAuthoritativeDenial({
              memberId: viewerMemberId,
              journeyId: scopedId,
            });
          }
          if (requestId !== generation.current) return;
          setPhase(kind === 'not-found' ? 'not-found' : 'forbidden');
          return;
        }
        if (mayUseJourneyOfflineFallback(cause) && viewerMemberId) {
          try {
            const cached = await readTodayOfflineSnapshot({
              memberId: viewerMemberId,
              journeyId: scopedId,
            });
            if (requestId !== generation.current) return;
            if (cached?.record?.payload?.journey && cached?.record?.payload?.schedule) {
              applyCachedToday(cached.record, cached.freshness);
              return;
            }
            if (cached?.freshness === OFFLINE_FRESHNESS.EXPIRED) {
              setError(OFFLINE_READ_COPY.expiredBody);
              setErrorModel(null);
              setPhase('error');
              setReadSource('live');
              return;
            }
          } catch {
            /* fall through */
          }
        }
        setReadSource('live');
        setError('');
        setErrorModel(mapApiErrorToUiModel(cause, { t }));
        setPhase('error');
        return;
      }

      const normalized = normalizeJourneyWorkspace(journeyResult.value, viewerMemberId);
      setJourney(normalized);
      setScheduleState(sectionState(
        scheduleResult.status === 'fulfilled'
          ? { status: 'fulfilled', value: normalizePlanningSchedule(scheduleResult.value) }
          : scheduleResult,
        '行程暫時無法載入。',
      ));
      setStaysState(sectionState(
        staysResult.status === 'fulfilled'
          ? { status: 'fulfilled', value: unwrapList(staysResult.value).map(normalizeAccommodationSummary) }
          : staysResult,
        '住宿暫時無法載入。',
      ));
      setTransportsState(sectionState(
        transportsResult.status === 'fulfilled'
          ? { status: 'fulfilled', value: unwrapList(transportsResult.value).map(normalizeTransportSummary) }
          : transportsResult,
        '交通暫時無法載入。',
      ));
      setChecklistState(sectionState(
        checklistResult.status === 'fulfilled'
          ? { status: 'fulfilled', value: normalizeChecklistBoard(checklistResult.value).items }
          : checklistResult,
        '待辦暫時無法載入。',
      ));
      setDecisionsState(sectionState(
        decisionsResult.status === 'fulfilled'
          ? { status: 'fulfilled', value: normalizeDecisionList(decisionsResult.value) }
          : decisionsResult,
        '決策暫時無法載入。',
      ));
      setSituationsState(sectionState(
        situationsResult.status === 'fulfilled'
          ? { status: 'fulfilled', value: unwrapSituationList(situationsResult.value) }
          : situationsResult,
        '旅程狀況暫時無法載入。',
      ));
      setReadSource('live');
      setOfflineFreshness(null);
      setOfflineFetchedAt('');
      setPhase('ready');

      if (scheduleResult.status === 'fulfilled') {
        writeTodayOfflineSnapshot({
          memberId: viewerMemberId,
          journeyId: scopedId,
          journeyRaw: journeyResult.value,
          scheduleRaw: scheduleResult.value,
          staysRaw: staysResult.status === 'fulfilled' ? staysResult.value : undefined,
          transportsRaw: transportsResult.status === 'fulfilled' ? transportsResult.value : undefined,
          checklistRaw: checklistResult.status === 'fulfilled' ? checklistResult.value : undefined,
          decisionsRaw: decisionsResult.status === 'fulfilled' ? decisionsResult.value : undefined,
          situationsRaw: situationsResult.status === 'fulfilled' ? situationsResult.value : undefined,
        }).catch(() => {
          /* cache write must not break live Today */
        });
      }
    } catch (cause) {
      if (requestId !== generation.current) return;
      setReadSource('live');
      setError('');
      setErrorModel(mapApiErrorToUiModel(cause, { t }));
      setPhase('error');
    }
  }, [applyCachedToday, journeyId, t, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    const onOnline = () => {
      load();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onOnline);
    }
    return () => {
      generation.current += 1;
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline);
      }
    };
  }, [journeyId, load]);

  const refetchAuthoritativeSchedule = useCallback(async () => {
    const scopedId = String(journeyId || '');
    const raw = await journeyPlanningApi.getSchedule(scopedId);
    if (String(journeyId) !== scopedId) return null;
    const normalized = normalizePlanningSchedule(raw);
    setScheduleState({ status: 'ready', data: normalized, error: '' });
    return normalized;
  }, [journeyId]);

  const refetchAuthoritativeJourney = useCallback(async () => {
    const scopedId = String(journeyId || '');
    const payload = await journeyApi.getDetail(scopedId);
    if (String(journeyId) !== scopedId) return null;
    const normalized = normalizeJourneyWorkspace(payload, viewerMemberId);
    setJourney(normalized);
    return normalized;
  }, [journeyId, viewerMemberId]);

  const runActivityExecution = useCallback(async (activity, action, { cancellationReason = null } = {}) => {
    if (readSource === 'cached') return;
    if (executionLock.current || lifecycleLock.current) return;
    const activityId = String(activity?.activityId || '');
    if (!activityId) return;

    const expectedRowVersion = scheduleState.data?.itineraryRowVersion;
    if (expectedRowVersion == null) {
      setExecutionFeedback({
        kind: 'error',
        message: TODAY_ACTIVITY_EXECUTION_COPY.missingVersion,
      });
      return;
    }

    executionLock.current = true;
    setExecutionBusyActivityId(activityId);
    setExecutionFeedback(null);

    try {
      const payload = { expectedRowVersion };
      if (action === ACTIVITY_EXECUTION_ACTIONS.CONFIRM) {
        await journeyPlanningApi.confirmActivity(journeyId, activityId, payload);
      } else if (action === ACTIVITY_EXECUTION_ACTIONS.START) {
        await journeyPlanningApi.startActivity(journeyId, activityId, payload);
      } else if (action === ACTIVITY_EXECUTION_ACTIONS.COMPLETE) {
        await journeyPlanningApi.completeActivity(journeyId, activityId, payload);
      } else if (action === ACTIVITY_EXECUTION_ACTIONS.CANCEL) {
        await journeyPlanningApi.cancelActivity(journeyId, activityId, {
          expectedRowVersion,
          cancellationReason,
        });
        setCancelTarget(null);
        setCancelReason('');
      } else {
        return;
      }

      // Mutation succeeded. Authoritative schedule refetch is mandatory for shared itinerary CAS.
      // Do not invent the next rowVersion from the mutation response.
      try {
        await refetchAuthoritativeSchedule();
        setExecutionFeedback({
          kind: 'success',
          message: successMessageForAction(action),
        });
      } catch {
        setScheduleState((prev) => ({
          status: prev.status === 'ready' ? 'ready' : prev.status,
          data: prev.data
            ? { ...prev.data, itineraryRowVersion: null }
            : null,
          error: prev.error || '',
        }));
        setExecutionFeedback({
          kind: 'error',
          message: TODAY_ACTIVITY_EXECUTION_COPY.refreshFailed,
        });
      }
    } catch (cause) {
      const kind = classifyActivityExecutionError(cause);
      const message = executionFailureMessage(kind, cause);
      setExecutionFeedback({ kind: kind === 'error' ? 'error' : kind, message });
      if (kind === 'conflict') {
        try {
          await refetchAuthoritativeSchedule();
        } catch {
          setScheduleState((prev) => ({
            status: prev.status === 'ready' ? 'ready' : prev.status,
            data: prev.data
              ? { ...prev.data, itineraryRowVersion: null }
              : null,
            error: prev.error || '',
          }));
          // Keep conflict message; never invent a refreshed version. Never silent-retry.
        }
      }
      // Do not patch local activity status to a successful transition on failure.
    } finally {
      executionLock.current = false;
      setExecutionBusyActivityId('');
    }
  }, [journeyId, readSource, refetchAuthoritativeSchedule, scheduleState.data?.itineraryRowVersion]);

  const submitTodayLifecycle = useCallback(async (action) => {
    if (readSource === 'cached') return;
    if (!journey || !action || lifecycleLock.current || executionLock.current) return;
    if (action !== 'START' && action !== 'COMPLETE') return;

    const expectedRowVersion = journey.rowVersion;
    if (expectedRowVersion == null) {
      setLifecycleFeedback({
        kind: 'error',
        message: lifecycleMutationErrorMessage('validation', '旅程版本尚未就緒，請稍後再試。'),
      });
      return;
    }

    const requestId = generation.current;
    const body = buildLifecycleMutationBody(expectedRowVersion, action);
    lifecycleLock.current = true;
    setLifecyclePending(true);
    setLifecycleFeedback(null);

    try {
      if (action === 'START') {
        await journeyApi.start(journey.journeyId, body);
      } else {
        await journeyApi.complete(journey.journeyId, body);
      }
      if (requestId !== generation.current) return;
      setLifecycleConfirmAction(null);
      // Accept success including noOp=true; still require authoritative journey refetch.
      try {
        await refetchAuthoritativeJourney();
        if (requestId !== generation.current) return;
        setLifecycleFeedback({
          kind: 'success',
          message: action === 'START' ? '旅程已開始。' : '旅程已完成。',
        });
      } catch {
        if (requestId !== generation.current) return;
        setJourney((prev) => (prev ? { ...prev, rowVersion: null } : prev));
        setLifecycleFeedback({
          kind: 'error',
          message: '操作可能已送出，但無法重新載入最新旅程狀態。請重新整理後再試，勿使用舊版本繼續操作。',
        });
      }
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyLifecycleMutationError(cause);
      const message = lifecycleMutationErrorMessage(kind, null);
      if (kind === 'conflict' || kind === 'invalid-lifecycle') {
        setLifecycleConfirmAction(null);
        setLifecycleFeedback({ kind: 'conflict', message });
        try {
          await refetchAuthoritativeJourney();
        } catch {
          setJourney((prev) => (prev ? { ...prev, rowVersion: null } : prev));
          // Keep conflict message; never invent version / silent-retry.
        }
      } else if (kind === 'not-found') {
        setLifecycleConfirmAction(null);
        setPhase('not-found');
      } else if (kind === 'forbidden' || kind === 'unauthorized') {
        setLifecycleConfirmAction(null);
        setLifecycleFeedback({ kind: 'forbidden', message });
      } else {
        setLifecycleFeedback({
          kind: kind === 'network' ? 'network' : 'error',
          message,
        });
      }
    } finally {
      lifecycleLock.current = false;
      setLifecyclePending(false);
    }
  }, [journey, readSource, refetchAuthoritativeJourney]);

  const clock = useMemo(
    () => resolveJourneyClock({
      timezone: journey?.timezone,
      now: now || new Date(),
      deviceTimeZone,
    }),
    [journey?.timezone, now, deviceTimeZone],
  );

  const scheduleDays = scheduleState.data?.days;
  const fallbackDays = (journey?.days || []).map((day) => ({
    journeyDayId: day.journeyDayId || day.dayId,
    date: day.date,
    displayOrder: day.displayOrder ?? day.sequence,
    title: day.title,
  }));

  const daySelection = useMemo(() => selectJourneyDay({
    lifecycleStatus: journey?.lifecycleStatus,
    calendarDate: clock.calendarDate,
    startDate: journey?.startDate,
    days: scheduleDays || fallbackDays,
    historicalDayId,
  }), [journey, clock.calendarDate, scheduleDays, fallbackDays, historicalDayId]);

  const selectedDay = daySelection.selectedDay;
  const selectedActivities = useMemo(
    () => activitiesForSelectedDay(scheduleState.data, selectedDay?.journeyDayId),
    [scheduleState.data, selectedDay?.journeyDayId],
  );
  const nextActivity = useMemo(
    () => (daySelection.mode === 'LIVE' || daySelection.mode === 'PREVIEW' || daySelection.mode === 'HISTORICAL'
      ? selectNextActivity(selectedActivities, clock.clockTime)
      : null),
    [daySelection.mode, selectedActivities, clock.clockTime],
  );
  const selectedDate = selectedDay?.date || clock.calendarDate;
  const tonightStays = useMemo(
    () => staysForCalendarDate(staysState.data, selectedDate),
    [staysState.data, selectedDate],
  );
  const todayTransports = useMemo(
    () => transportsForCalendarDate(transportsState.data, selectedDate),
    [transportsState.data, selectedDate],
  );
  const checklistItems = useMemo(
    () => checklistItemsForToday(checklistState.data, selectedDate),
    [checklistState.data, selectedDate],
  );
  const attentionDecisions = useMemo(
    () => decisionsNeedingAttention(decisionsState.data),
    [decisionsState.data],
  );
  const attentionSituations = useMemo(
    () => situationsNeedingAttention(situationsState.data),
    [situationsState.data],
  );
  const copy = todayModeCopy(daySelection.mode, journey?.lifecycleStatus);
  const canJournal = !offlineCached && WRITABLE_MEMORY_LIFECYCLES.includes(journey?.lifecycleStatus);
  const journalHref = canJournal
    ? journalComposeHref(journey?.journeyId, {
      journeyDayId: selectedDay?.journeyDayId,
      activityId: nextActivity?.activityId,
    })
    : `/journeys/${journey?.journeyId}/journal`;
  const liveLifecycleAction = todayJourneyLifecycleAction(journey);
  const actorReady = viewerMemberId != null && viewerMemberId !== '';
  const executionBusy = Boolean(executionBusyActivityId);
  const lifecycleBusy = lifecyclePending;
  const mutationBusy = executionBusy || lifecycleBusy || offlineCached;
  const lifecycleAction = offlineCached ? null : liveLifecycleAction;
  const lifecycleConfirmCopyText = lifecycleConfirmAction
    ? lifecycleConfirmCopy(lifecycleConfirmAction)
    : null;

  function actionsForActivity(activity) {
    if (offlineCached) return [];
    if (!actorReady || !journey?.requesterRole) {
      return [];
    }
    return resolveActivityExecutionCapabilities({
      activityStatus: activity?.status,
      journeyLifecycle: journey?.lifecycleStatus,
      requesterRole: journey?.requesterRole,
      actorMemberId: viewerMemberId,
      createdByMemberId: activity?.createdByMemberId,
      includePermission: true,
    }).actions;
  }

  function openCancelDialog(activity) {
    if (mutationBusy || offlineCached) return;
    setCancelTarget(activity);
    setCancelReason('');
    setExecutionFeedback(null);
  }

  function openLifecycleConfirm(action) {
    if (!action || mutationBusy || offlineCached) return;
    setLifecycleFeedback(null);
    setLifecycleConfirmAction(action);
  }

  function closeLifecycleConfirm() {
    if (lifecycleLock.current) return;
    setLifecycleConfirmAction(null);
  }

  function renderExternalNavLink(activity, { prominent = false } = {}) {
    if (!isActivityExternalMapNavigationEligible(activity)) return null;
    if (offlineCached) {
      return (
        <span
          className={prominent
            ? 'journey-today__nav-cta journey-today__nav-cta--hero offline-read-network-required'
            : 'offline-read-network-required'}
          role="status"
          data-testid={prominent ? 'today-next-nav-offline' : 'today-activity-nav-offline'}
          data-activity-id={activity.activityId}
          aria-label={`${OFFLINE_READ_COPY.networkRequired}：${activity.title}`}
        >
          {OFFLINE_READ_COPY.networkRequired}
        </span>
      );
    }
    const resolved = resolveExternalMapNavigation(activity);
    if (!resolved.available || !resolved.href) return null;
    return (
      <a
        className={prominent
          ? 'journey-today__nav-cta journey-today__nav-cta--hero'
          : 'journey-today__nav-cta'}
        href={resolved.href}
        target={EXTERNAL_MAP_NAVIGATION_LINK.target}
        rel={EXTERNAL_MAP_NAVIGATION_LINK.rel}
        data-testid={prominent ? 'today-next-nav' : 'today-activity-nav'}
        data-activity-id={activity.activityId}
        data-nav-source={resolved.source}
        aria-label={`帶我去 ${activity.title}`}
      >
        帶我去
      </a>
    );
  }

  function renderActivityActions(activity) {
    const actions = actionsForActivity(activity);
    const navLink = renderExternalNavLink(activity);
    if (!actions.length && !navLink) return null;
    const busyHere = executionBusyActivityId === String(activity.activityId);
    return (
      <div className="journey-today__activity-actions">
        {navLink}
        {actions.map((action) => {
          const label = activityExecutionActionLabel(action);
          const isCancel = action === ACTIVITY_EXECUTION_ACTIONS.CANCEL;
          return (
            <button
              key={action}
              type="button"
              data-execution-action={action}
              data-activity-id={activity.activityId}
              aria-label={`${label}：${activity.title}`}
              disabled={mutationBusy}
              aria-busy={busyHere || undefined}
              onClick={() => {
                if (isCancel) openCancelDialog(activity);
                else runActivityExecution(activity, action);
              }}
            >
              {busyHere && !isCancel ? TODAY_ACTIVITY_EXECUTION_COPY.working : label}
            </button>
          );
        })}
      </div>
    );
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state">
        <LoadingState label="載入中…" />
      </main>
    );
  }

  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.notFoundTitle}</h1>
        <p>{WORKSPACE_PRODUCT_CHROME.notFoundBody}</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }

  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.forbiddenTitle}</h1>
        <p>{WORKSPACE_PRODUCT_CHROME.forbiddenBody}</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }

  if (phase === 'error') {
    if (errorModel) {
      const errorCopy = resolveUiErrorCopy(errorModel, t);
      return (
        <main className="journey-workspace-state" data-testid="today-error">
          <ErrorState
            title="無法開啟今天"
            description={errorCopy.description}
            retryable
            retryLabel={WORKSPACE_PRODUCT_CHROME.tryAgain}
            onRetry={load}
          />
        </main>
      );
    }
    return (
      <main className="journey-workspace-state" role="alert" data-testid="today-error">
        <h1>無法開啟今天</h1>
        <p>{error}</p>
        <button type="button" onClick={load}>{WORKSPACE_PRODUCT_CHROME.tryAgain}</button>
      </main>
    );
  }

  const showLiveSections = ['LIVE', 'PREVIEW', 'HISTORICAL', 'NO_DAY'].includes(daySelection.mode);

  return (
    <main className="journey-workspace journey-today" data-read-source={readSource}>
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">{copy.eyebrow}</p>
          <h1>{copy.title}</h1>
          <dl className="journey-workspace__facts">
            <div>
              <dt>日期</dt>
              <dd>
                <time dateTime={clock.calendarDate}>{clock.calendarDate}</time>
                {clock.clockTime ? <span> {clock.clockTime}</span> : null}
              </dd>
            </div>
            <div>
              <dt>旅程狀態</dt>
              <dd>
                <span data-lifecycle-status={journey.lifecycleStatus || 'UNKNOWN'}>
                  {journey.lifecycleProductLabel || journey.lifecycleLabel}
                </span>
              </dd>
            </div>
            <div>
              <dt>時間來源</dt>
              <dd>
                {clock.usesJourneyTimezone ? `旅程時區 ${clock.clockHint}` : clock.clockHint}
                <span className="visually-hidden">（非後端權威時間）</span>
              </dd>
            </div>
          </dl>
          {copy.hint ? <p className="journey-today__hint">{copy.hint}</p> : null}
        </div>
        <Link className="journey-workspace__back" to="/journeys">{WORKSPACE_PRODUCT_CHROME.myJourneys}</Link>
      </header>

      {offlineCached ? (
        <OfflineReadBanner
          fetchedAt={offlineFetchedAt}
          freshness={offlineFreshness || OFFLINE_FRESHNESS.STALE}
          onRetry={() => load()}
          retryBusy={phase === 'loading'}
        />
      ) : null}

      <JourneyWorkspaceNav
        journeyId={journey.journeyId}
        unreadCount={offlineCached ? null : unreadCount}
        offlineCached={offlineCached}
        todayOfflineAvailable
      />

      {lifecycleAction ? (
        <section
          className="journey-today__lifecycle"
          aria-label="旅程狀態操作"
          data-testid="today-lifecycle"
        >
          {lifecycleFeedback ? (
            <p
              className="journey-today__execution-feedback"
              role="status"
              data-kind={lifecycleFeedback.kind}
              data-testid="today-lifecycle-feedback"
            >
              {lifecycleFeedback.message}
            </p>
          ) : null}
          <button
            type="button"
            className="journey-today__lifecycle-cta"
            data-lifecycle-action={lifecycleAction}
            data-testid="today-lifecycle-cta"
            disabled={mutationBusy}
            aria-busy={lifecycleBusy || undefined}
            onClick={() => openLifecycleConfirm(lifecycleAction)}
          >
            {lifecycleBusy && lifecycleConfirmAction === lifecycleAction
              ? '更新中…'
              : lifecycleActionLabel(lifecycleAction)}
          </button>
        </section>
      ) : offlineCached && liveLifecycleAction ? (
        <section
          className="journey-today__lifecycle"
          aria-label="旅程狀態操作"
          data-testid="today-lifecycle-offline"
        >
          <p className="offline-read-network-required" role="status">
            {lifecycleActionLabel(liveLifecycleAction)}
            {' — '}
            {OFFLINE_READ_COPY.networkRequired}
          </p>
        </section>
      ) : lifecycleFeedback ? (
        <p
          className="journey-today__execution-feedback"
          role="status"
          data-kind={lifecycleFeedback.kind}
          data-testid="today-lifecycle-feedback"
        >
          {lifecycleFeedback.message}
        </p>
      ) : null}

      {!offlineCached ? (
        <p className="journey-today__timeline-entry">
          <Link to={`/journeys/${journey.journeyId}/timeline`}>查看旅程動態</Link>
        </p>
      ) : null}

      {!offlineCached && unreadCount > 0 ? (
        <p className="journey-notice__banner">
          <Link to={`/journeys/${journey.journeyId}/notices`}>
            有 {unreadCount} 則旅程通知
          </Link>
        </p>
      ) : null}

      {daySelection.mode === 'UNAVAILABLE' || daySelection.mode === 'TERMINAL' || daySelection.mode === 'UNKNOWN' ? (
        <section className="journey-today__empty" aria-labelledby="today-unavailable">
          <p id="today-unavailable">{copy.hint}</p>
          <div className="journey-today__links">
            <Link to={`/journeys/${journey.journeyId}`}>{WORKSPACE_PRODUCT_CHROME.overview}</Link>
            <Link to={`/journeys/${journey.journeyId}/planning`}>{WORKSPACE_PRODUCT_CHROME.planning}</Link>
          </div>
        </section>
      ) : null}

      {daySelection.mode === 'HISTORICAL' && (scheduleState.data?.days || []).length > 0 ? (
        <div className="journey-today__day-picker">
          <label htmlFor={daySelectId}>選擇要回顧的一天</label>
          <select
            id={daySelectId}
            value={selectedDay?.journeyDayId || ''}
            onChange={(event) => setHistoricalDayId(event.target.value)}
          >
            {(scheduleState.data?.days || []).map((day) => (
              <option key={day.journeyDayId} value={day.journeyDayId}>
                {day.date}{day.title ? ` · ${day.title}` : ''}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {showLiveSections && nextActivity ? (
        <section className="journey-today__hero" aria-labelledby="today-next">
          <p className="journey-today__kicker">接下來</p>
          <h2 id="today-next">{nextActivity.title}</h2>
          <p className="journey-today__hero-meta">
            {nextActivity.plannedStartTime ? <time>{nextActivity.plannedStartTime}</time> : <span>時間未定</span>}
            <span>{activityTypeLabel(nextActivity.activityType)}</span>
            <span>{activityStatusLabel(nextActivity.status)}</span>
          </p>
          {nextActivity.locationLabel ? (
            <p className="journey-today__wrap">{nextActivity.locationLabel}</p>
          ) : null}
          {renderExternalNavLink(nextActivity, { prominent: true })}
          <p className="journey-today__disclaimer">依行程時間排序，不是即時定位。</p>
        </section>
      ) : null}

      {showLiveSections ? (
        <section className="journey-today__panel" aria-labelledby="today-schedule">
          <div className="journey-today__panel-head">
            <h2 id="today-schedule">今日行程</h2>
            <Link to={`/journeys/${journey.journeyId}/planning`}>{WORKSPACE_PRODUCT_CHROME.planning}</Link>
          </div>
          {executionFeedback ? (
            <p
              className="journey-today__execution-feedback"
              role="status"
              data-kind={executionFeedback.kind}
              data-testid="today-execution-feedback"
            >
              {executionFeedback.message}
            </p>
          ) : null}
          {scheduleState.status === 'error' ? (
            <p role="status">行程無法載入。{scheduleState.error}</p>
          ) : daySelection.mode === 'NO_DAY' || !selectedDay ? (
            <p className="journey-today__empty-copy">今天沒有對應的 Journey Day。</p>
          ) : selectedActivities.length === 0 ? (
            <p className="journey-today__empty-copy">這一天還沒有安排活動。</p>
          ) : (
            <ol className="journey-today__list">
              {selectedActivities.map((activity) => (
                <li key={activity.activityId} data-activity-id={activity.activityId} data-activity-status={activity.status}>
                  <p className="journey-today__time">
                    {activity.plannedStartTime || '時間未定'}
                    {activity.plannedEndTime ? ` – ${activity.plannedEndTime}` : ''}
                  </p>
                  <h3 className="journey-today__wrap">{activity.title}</h3>
                  <p>
                    {activityTypeLabel(activity.activityType)}
                    {' · '}
                    {activityStatusLabel(activity.status)}
                  </p>
                  {activity.locationLabel ? <p className="journey-today__wrap">{activity.locationLabel}</p> : null}
                  {renderActivityActions(activity)}
                </li>
              ))}
            </ol>
          )}
        </section>
      ) : null}

      {showLiveSections && (transportsState.status === 'error' || todayTransports.length > 0) ? (
        <section className="journey-today__panel" aria-labelledby="today-transport">
          <div className="journey-today__panel-head">
            <h2 id="today-transport">今天的交通</h2>
            <Link to={`/journeys/${journey.journeyId}/transport`}>{WORKSPACE_PRODUCT_CHROME.transport}</Link>
          </div>
          {transportsState.status === 'error' ? (
            <p role="status">交通無法載入。{transportsState.error}</p>
          ) : (
            <ul className="journey-today__list">
              {todayTransports.map((item) => (
                <li key={item.transportId}>
                  <h3 className="journey-today__wrap">{item.transportModeLabel || item.transportMode || '交通'}</h3>
                  <p>{[
                    item.carrierText,
                    item.departureDisplay ? formatDateTime(item.departureDisplay) : null,
                    item.arrivalDisplay ? formatDateTime(item.arrivalDisplay) : null,
                  ].filter(Boolean).join(' · ')}</p>
                  {item.bookingReference ? <p>編號 {item.bookingReference}</p> : null}
                  <p className="journey-today__disclaimer">僅顯示已記錄的班次資訊，不是即時動態。</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {showLiveSections && (staysState.status === 'error' || tonightStays.length > 0) ? (
        <section className="journey-today__panel" aria-labelledby="today-stay">
          <div className="journey-today__panel-head">
            <h2 id="today-stay">今晚住宿</h2>
            <Link to={`/journeys/${journey.journeyId}/stays`}>{WORKSPACE_PRODUCT_CHROME.stays}</Link>
          </div>
          {staysState.status === 'error' ? (
            <p role="status">住宿無法載入。{staysState.error}</p>
          ) : tonightStays.length > 1 ? (
            <>
              <p role="status">今晚有多筆重疊住宿，請核對。</p>
              <ul className="journey-today__list">
                {tonightStays.map((stay) => (
                  <li key={stay.accommodationId}>
                    <h3 className="journey-today__wrap">{stay.propertyName}</h3>
                    <p>{[
                      stay.checkInDisplay ? formatDateTime(stay.checkInDisplay) : null,
                      stay.checkOutDisplay ? formatDateTime(stay.checkOutDisplay) : null,
                    ].filter(Boolean).join(' → ')}</p>
                    {stay.confirmationReference ? <p>確認號 {stay.confirmationReference}</p> : null}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div>
              <h3 className="journey-today__wrap">{tonightStays[0].propertyName}</h3>
              <p>{[
                tonightStays[0].checkInDisplay ? formatDateTime(tonightStays[0].checkInDisplay) : null,
                tonightStays[0].checkOutDisplay ? formatDateTime(tonightStays[0].checkOutDisplay) : null,
              ].filter(Boolean).join(' → ')}</p>
              {tonightStays[0].confirmationReference ? <p>確認號 {tonightStays[0].confirmationReference}</p> : null}
            </div>
          )}
        </section>
      ) : null}

      {showLiveSections && (checklistState.status === 'error' || checklistItems.length > 0) ? (
        <section className="journey-today__panel" aria-labelledby="today-checklist">
          <div className="journey-today__panel-head">
            <h2 id="today-checklist">還要記得</h2>
            <Link to={`/journeys/${journey.journeyId}/checklist`}>{WORKSPACE_PRODUCT_CHROME.checklist}</Link>
          </div>
          {checklistState.status === 'error' ? (
            <p role="status">待辦無法載入。{checklistState.error}</p>
          ) : (
            <ul className="journey-today__list">
              {checklistItems.map((item) => (
                <li key={item.itemId}>
                  <h3 className="journey-today__wrap">{item.title}</h3>
                  {item.urgency === 'OVERDUE' ? <p>已過期{item.dueAt ? ` · ${formatDateTime(item.dueAt)}` : ''}</p> : null}
                  {item.urgency === 'DUE_TODAY' ? <p>今天到期{item.dueAt ? ` · ${formatDateTime(item.dueAt)}` : ''}</p> : null}
                  {item.urgency === 'NONE' ? <p>未完成</p> : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {showLiveSections && (decisionsState.status === 'error' || attentionDecisions.length > 0) ? (
        <section className="journey-today__panel" aria-labelledby="today-decisions">
          <div className="journey-today__panel-head">
            <h2 id="today-decisions">需要你決定</h2>
            <Link to={`/journeys/${journey.journeyId}/decisions`}>{DECISIONS_PRODUCT_CHROME.title}</Link>
          </div>
          {decisionsState.status === 'error' ? (
            <p role="status">決策無法載入。{decisionsState.error}</p>
          ) : (
            <ul className="journey-today__list">
              {attentionDecisions.map((item) => (
                <li key={item.decisionId}>
                  <h3 className="journey-today__wrap">
                    <Link to={`/journeys/${journey.journeyId}/decisions/${item.decisionId}`}>
                      {item.title}
                    </Link>
                  </h3>
                  <p>
                    {decisionStatusProductLabel(item.decisionStatus)}
                    {item.decisionType === 'CONTINGENCY' ? ' · 臨時狀況' : ''}
                    {item.deadlineAt ? ` · 截止 ${formatDateTime(item.deadlineAt)}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {showLiveSections && (situationsState.status === 'error' || attentionSituations.length > 0) ? (
        <section className="journey-today__panel" aria-labelledby="today-situations">
          <div className="journey-today__panel-head">
            <h2 id="today-situations">旅程狀況</h2>
            <Link to={`/journeys/${journey.journeyId}/situations`}>查看全部</Link>
          </div>
          {situationsState.status === 'error' ? (
            <p role="status">旅程狀況無法載入。{situationsState.error}</p>
          ) : (
            <ul className="journey-today__list">
              {attentionSituations.map((item) => (
                <li key={item.situationId}>
                  <h3 className="journey-today__wrap">
                    <Link to={item.detailRoute || `/journeys/${journey.journeyId}/situations/${item.situationId}`}>
                      {item.title}
                    </Link>
                  </h3>
                  <p>
                    {item.situationStatusLabel}
                    <span className="visually-hidden"> ({item.situationStatus})</span>
                    {item.severityLabel ? ` · ${item.severityLabel}` : ''}
                    {item.situationTypeLabel ? ` · ${item.situationTypeLabel}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {canJournal ? (
        <div className="journey-today__record">
          <Link className="journey-today__record-link" to={journalHref}>記錄今天</Link>
        </div>
      ) : null}

      {cancelTarget ? (
        <div
          className="journey-today__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby={cancelTitleId}
          data-testid="today-cancel-dialog"
        >
          <div className="journey-today__dialog-card">
            <h3 id={cancelTitleId}>{TODAY_ACTIVITY_EXECUTION_COPY.cancelDialogTitle}</h3>
            <p>
              {TODAY_ACTIVITY_EXECUTION_COPY.cancelDialogBody}
              {' '}
              「
              <span className="journey-today__wrap">{cancelTarget.title}</span>
              」
            </p>
            <label htmlFor={cancelReasonId}>
              {TODAY_ACTIVITY_EXECUTION_COPY.cancelReasonLabel}
              <textarea
                id={cancelReasonId}
                value={cancelReason}
                maxLength={500}
                disabled={mutationBusy}
                onChange={(event) => setCancelReason(event.target.value)}
              />
            </label>
            <div className="journey-today__dialog-actions">
              <button
                type="button"
                data-primary="true"
                data-testid="today-confirm-cancel"
                disabled={mutationBusy}
                aria-busy={executionBusyActivityId === String(cancelTarget.activityId) || undefined}
                onClick={() => {
                  const reason = cancelReason.trim();
                  runActivityExecution(cancelTarget, ACTIVITY_EXECUTION_ACTIONS.CANCEL, {
                    cancellationReason: reason || null,
                  });
                }}
              >
                {executionBusyActivityId === String(cancelTarget.activityId)
                  ? TODAY_ACTIVITY_EXECUTION_COPY.cancelling
                  : TODAY_ACTIVITY_EXECUTION_COPY.confirmCancel}
              </button>
              <button
                type="button"
                disabled={mutationBusy}
                onClick={() => {
                  setCancelTarget(null);
                  setCancelReason('');
                }}
              >
                {TODAY_ACTIVITY_EXECUTION_COPY.dismissCancel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {lifecycleConfirmAction && lifecycleConfirmCopyText ? (
        <div className="journey-workspace-lifecycle-confirm" role="presentation">
          <button
            type="button"
            className="journey-workspace-lifecycle-confirm__backdrop"
            aria-label="關閉確認"
            disabled={lifecycleBusy}
            onClick={closeLifecycleConfirm}
          />
          <div
            className="journey-workspace-lifecycle-confirm__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={lifecycleConfirmTitleId}
            data-testid="today-lifecycle-confirm"
          >
            <h3 id={lifecycleConfirmTitleId}>{lifecycleConfirmCopyText.title}</h3>
            <p>{lifecycleConfirmCopyText.body}</p>
            {lifecycleFeedback ? (
              <p className="journey-workspace__error" role="alert">{lifecycleFeedback.message}</p>
            ) : null}
            <div className="journey-workspace-lifecycle-confirm__actions">
              <button type="button" disabled={lifecycleBusy} onClick={closeLifecycleConfirm}>
                取消
              </button>
              <button
                type="button"
                className="journey-workspace__lifecycle-primary"
                data-testid="today-lifecycle-confirm-submit"
                disabled={lifecycleBusy}
                aria-busy={lifecycleBusy || undefined}
                onClick={() => submitTodayLifecycle(lifecycleConfirmAction)}
              >
                {lifecycleBusy ? '更新中…' : `確認${lifecycleActionLabel(lifecycleConfirmAction)}`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
